import { FiltroDeListado } from "@/components/FiltroDeListado";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/native-select";
import type { AutorDelComentario, OpcionDeFeedback } from "@/lib/feedback";

interface FiltrosDelTableroProps {
  modulos: OpcionDeFeedback[];
  tipos: OpcionDeFeedback[];
  autores: AutorDelComentario[];
  /** Valores actuales, tal como vienen de la URL (`""` = sin filtrar). */
  valores: { modulo: string; tipo: string; autor: string };
  onCambiar: (clave: "modulo" | "tipo" | "autor", valor: string) => void;
  onLimpiar: () => void;
}

/**
 * Los tres filtros del tablero: módulo, tipo y quién lo publicó.
 *
 * Filtra el SERVIDOR: acá solo se traducen los controles a query params. Un
 * filtrado en memoria sobre la página cargada mentiría —diría "1 resultado"
 * mirando 25 de 300 filas—.
 *
 * `<option value="">Todos</option>` es la opción de no filtrar, y el valor
 * vacío no es casual: es el que `fetchFeedback` sabe que NO tiene que mandar.
 * Con un centinela tipo `"todos"`, el backend lo validaría contra el enum y
 * respondería 422.
 *
 * Los autores son quienes efectivamente comentaron, no todos los usuarios:
 * ofrecer a alguien que nunca escribió es prometer un resultado que el
 * tablero no tiene.
 */
export function FiltrosDelTablero({
  modulos,
  tipos,
  autores,
  valores,
  onCambiar,
  onLimpiar,
}: FiltrosDelTableroProps) {
  const hayFiltro = Boolean(valores.modulo || valores.tipo || valores.autor);

  return (
    /* `section` con nombre y no un `div`: el formulario de alta también tiene
       un control rotulado "Módulo" y otro "Tipo". Sin dos regiones nombradas,
       ni un lector de pantalla ni un test pueden decir cuál de los dos
       "Módulo" están tocando. */
    <section
      aria-label="Filtros del tablero"
      className="flex flex-wrap items-center gap-3"
    >
      <FiltroDeListado etiqueta="Módulo" id="filtro-modulo">
        <NativeSelect
          id="filtro-modulo"
          size="sm"
          value={valores.modulo}
          onChange={(evento) => onCambiar("modulo", evento.target.value)}
        >
          <option value="">Todos</option>
          {modulos.map((modulo) => (
            <option key={modulo.valor} value={modulo.valor}>
              {modulo.etiqueta}
            </option>
          ))}
        </NativeSelect>
      </FiltroDeListado>

      <FiltroDeListado etiqueta="Tipo" id="filtro-tipo">
        <NativeSelect
          id="filtro-tipo"
          size="sm"
          value={valores.tipo}
          onChange={(evento) => onCambiar("tipo", evento.target.value)}
        >
          <option value="">Todos</option>
          {tipos.map((tipo) => (
            <option key={tipo.valor} value={tipo.valor}>
              {tipo.etiqueta}
            </option>
          ))}
        </NativeSelect>
      </FiltroDeListado>

      <FiltroDeListado etiqueta="Publicado por" id="filtro-autor">
        <NativeSelect
          id="filtro-autor"
          size="sm"
          value={valores.autor}
          onChange={(evento) => onCambiar("autor", evento.target.value)}
        >
          <option value="">Todos</option>
          {autores.map((autor) => (
            <option key={autor.id} value={autor.id}>
              {autor.nombre}
            </option>
          ))}
        </NativeSelect>
      </FiltroDeListado>

      {/* Aparece solo con algún filtro puesto: un botón que no hace nada es
          una promesa vacía, y con tres selects en "Todos" no hay nada que
          limpiar. */}
      {hayFiltro && (
        <Button variant="ghost" size="sm" onClick={onLimpiar}>
          Limpiar filtros
        </Button>
      )}
    </section>
  );
}
